import React, { ReactNode, useState } from 'react'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  BorderOverlayProps,
  TypographyProps,
  FontWeight,
  applyBackgroundStyle,
  applyBorderStyle,
  applyTypography,
  borderOverlayStyle,
} from 'breaktime-theme/src/puck/fields/StyleFields'
import {
  applySpacing,
  LayoutSpacing,
  buildStartScreenCardStyle,
  buildStartScreenTextStyle,
} from '../PollStartScreen/PollStartScreen'

// Start-screen transition + start-slide styles (shared with the non-puck
// StartSlideWrapper). Bundled so puck steps render the start screen correctly.
import 'steps/StartSlideWrapper.scss'

const ANIMATION_DURATION = 600

/**
 * Visual for the puck start screen — headline, body, and start button. Shared by
 * any puck step that exposes a `startScreen` slot (poll, listicle, …). Reads the
 * generic PollStartScreen / Card / Button slot props.
 */
export const StartScreenView = ({
  background,
  headline,
  body,
  cardStyle,
  titleStyle,
  bodyStyle,
  buttonLabel,
  buttonBackground,
  buttonBorder,
  buttonTextColor,
  buttonBgHoverColor,
  buttonFontFamily,
  buttonFontFamilyFallback,
  buttonTextTransform,
  buttonFontSize,
  buttonFontWeight,
  buttonLayout,
  wrapperLayout,
  cardOverlayStyle,
  buttonOverlayStyle,
  defaultButtonLabel = 'Start',
  onStart,
}: {
  background?: BackgroundStyleProps
  headline?: string
  body?: string
  cardStyle?: React.CSSProperties
  titleStyle?: React.CSSProperties
  bodyStyle?: React.CSSProperties
  buttonLabel?: string
  buttonBackground?: BackgroundStyleProps
  buttonBorder?: BorderStyleProps
  buttonTextColor?: string
  buttonBgHoverColor?: string
  buttonFontFamily?: TypographyProps['fontFamily']
  buttonFontFamilyFallback?: TypographyProps['fontFamilyFallback']
  buttonTextTransform?: TypographyProps['textTransform']
  buttonFontSize?: string
  buttonFontWeight?: FontWeight
  buttonLayout?: LayoutSpacing
  wrapperLayout?: LayoutSpacing
  cardOverlayStyle?: React.CSSProperties
  buttonOverlayStyle?: React.CSSProperties
  defaultButtonLabel?: string
  onStart: () => void
}) => (
  <div
    className="start-slide"
    style={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      ...applyBackgroundStyle(background),
    }}
  >
    <div className="container start-slide-container">
      {/* block flow so the card's own margins (+ wrapperLayout here) position
          the whole card block */}
      <div
        className="start-slide-inner-content-wrapper"
        style={{ width: '100%', ...applySpacing(wrapperLayout) }}
      >
        <div
          className="start-slide__content"
          style={{
            ...(cardStyle ?? {}),
            ...(cardOverlayStyle ? { position: 'relative' } : {}),
          }}
        >
          {cardOverlayStyle && <div aria-hidden style={cardOverlayStyle} />}
          {headline && (
            <h2
              className="start-screen-headline step-content__title"
              style={titleStyle}
              dangerouslySetInnerHTML={{ __html: headline }}
            />
          )}
          {body && (
            <p
              className="start-body start-screen-copy"
              style={bodyStyle}
              dangerouslySetInnerHTML={{ __html: body }}
            />
          )}
          <button
            className={`btn start-button${buttonBgHoverColor ? ' puck-start-button' : ''}`}
            onClick={onStart}
            style={{
              ...applyBackgroundStyle(buttonBackground),
              ...applyBorderStyle(buttonBorder),
              ...applyTypography({
                fontFamily: buttonFontFamily,
                fontFamilyFallback: buttonFontFamilyFallback,
                textTransform: buttonTextTransform,
              }),
              ...(buttonTextColor && { color: buttonTextColor }),
              ...(buttonFontSize && { fontSize: buttonFontSize }),
              ...(buttonFontWeight ? { fontWeight: Number(buttonFontWeight) || undefined } : {}),
              ...(buttonBgHoverColor && { '--puck-start-button-hover-bg': buttonBgHoverColor } as React.CSSProperties),
              ...applySpacing(buttonLayout),
              ...(buttonOverlayStyle ? { position: 'relative', overflow: 'visible' } : {}),
            }}
          >
            {buttonOverlayStyle && <span aria-hidden style={buttonOverlayStyle} />}
            {buttonLabel || defaultButtonLabel}
          </button>
        </div>
      </div>
    </div>
  </div>
)

/**
 * Wraps step content with the start-screen → content slide transition when a
 * PollStartScreen block sits in the given `startScreen` slot. Otherwise renders
 * the content directly.
 */
export const StartScreenTransition = ({
  startScreen,
  defaultButtonLabel = 'Start',
  children,
}: {
  startScreen?: { type: string; props?: Record<string, unknown> }[]
  defaultButtonLabel?: string
  children: ReactNode
}) => {
  const [startDone, setStartDone] = useState(false)

  const startScreenItem = (startScreen ?? []).find(
    i => i.type === 'PollStartScreen',
  )?.props as
    | {
        background?: BackgroundStyleProps
        card?: { type: string; props?: Record<string, unknown> }[]
      }
    | undefined

  if (!startScreenItem) return <>{children}</>

  const cardProps =
    ((startScreenItem.card ?? []).find(i => i.type === 'PollStartScreenCard')
      ?.props as Record<string, any> | undefined) ?? {}
  const buttonProps =
    ((cardProps.button ?? []).find(
      (i: { type: string }) => i.type === 'PollStartScreenButton',
    )?.props as Record<string, any> | undefined) ?? {}

  return (
    <div className="start-wrapper">
      <style
        dangerouslySetInnerHTML={{
          __html: `.transition-slide { transition: transform ${ANIMATION_DURATION}ms ease-in-out; }`,
        }}
      />
      <div
        className={`transition-slide transition-start-slide ${!startDone ? 'is--active' : 'is--inactive'}`}
      >
        <StartScreenView
          background={startScreenItem.background}
          headline={cardProps.headline}
          body={cardProps.body}
          cardStyle={buildStartScreenCardStyle({
            background: cardProps.background,
            border: cardProps.border,
            maxWidth: cardProps.maxWidth,
            layout: cardProps.layout,
          })}
          titleStyle={buildStartScreenTextStyle({
            fontFamily: cardProps.titleFontFamily,
            fontFamilyFallback: cardProps.titleFontFamilyFallback,
            textTransform: cardProps.titleTextTransform,
            fontSize: cardProps.titleFontSize,
            fontWeight: cardProps.titleFontWeight,
            color: cardProps.titleColor,
            lineHeight: cardProps.titleLineHeight,
          })}
          bodyStyle={buildStartScreenTextStyle({
            fontFamily: cardProps.bodyFontFamily,
            fontFamilyFallback: cardProps.bodyFontFamilyFallback,
            textTransform: cardProps.bodyTextTransform,
            fontSize: cardProps.bodyFontSize,
            fontWeight: cardProps.bodyFontWeight,
            color: cardProps.bodyColor,
            lineHeight: cardProps.bodyLineHeight,
          })}
          wrapperLayout={cardProps.wrapperLayout}
          cardOverlayStyle={borderOverlayStyle(
            cardProps.borderOverlay as BorderOverlayProps | undefined,
          )}
          buttonOverlayStyle={borderOverlayStyle(
            buttonProps.borderOverlay as BorderOverlayProps | undefined,
          )}
          buttonLabel={buttonProps.label}
          buttonBackground={buttonProps.background}
          buttonBorder={buttonProps.border}
          buttonTextColor={buttonProps.textColor}
          buttonBgHoverColor={buttonProps.bgHoverColor}
          buttonFontFamily={buttonProps.fontFamily}
          buttonFontFamilyFallback={buttonProps.fontFamilyFallback}
          buttonTextTransform={buttonProps.textTransform}
          buttonFontSize={buttonProps.fontSize}
          buttonFontWeight={buttonProps.fontWeight}
          buttonLayout={buttonProps.layout}
          defaultButtonLabel={defaultButtonLabel}
          onStart={() => setStartDone(true)}
        />
      </div>
      <div
        className={`transition-slide transition-content-slide ${startDone ? 'is--active' : 'is--inactive'}`}
      >
        {children}
      </div>
    </div>
  )
}
